import * as React from "react";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe }    from "@fortawesome/free-solid-svg-icons/faGlobe";
import { faLandmark } from "@fortawesome/free-solid-svg-icons/faLandmark";
import { faPenNib }   from "@fortawesome/free-solid-svg-icons/faPenNib";
import { faCompass }  from "@fortawesome/free-solid-svg-icons/faCompass";
import { faStore }    from "@fortawesome/free-solid-svg-icons/faStore";
import { faRoute }    from "@fortawesome/free-solid-svg-icons/faRoute";

// ─── Asset Imports ────────────────────────────────────────────────────────────
import advertiseImg from "../legacy/assets/rethinkways-contact-us-06-BhZKT7Wg.webp";
import contactImg   from "../legacy/assets/rethinkways-contact-us-07-BoZUxkkS.webp";
import whoWeAreImg  from "../legacy/assets/rethinkways-about-us-who-we-are-BMpLKXWc.png";
import assetUrl from "../utils/assetUrl";

const videoWebm = assetUrl("/assets/videos/travel-aboutus-video.webm");
const videoMp4  = assetUrl("/assets/videos/travel-aboutus-video.mp4");

// ─── Dark Mode Sync ───────────────────────────────────────────────────────────
// Identical to PrivacyPolicy — reads the same state the Header writes:
//   localStorage["trw-dark-mode"] === "1" | "0"
//   document.documentElement classList "trw-dark-mode"
//   document.body classList "trw-dark-mode" | "dark"

function getHeaderDarkMode() {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("trw-dark-mode");
  if (stored === "1") return true;
  if (stored === "0") return false;
  return (
    document.documentElement.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("dark")
  );
}

function useSyncedDarkMode() {
  const [isDark, setIsDark] = useState(getHeaderDarkMode);

  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(getHeaderDarkMode()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    const onStorage = (e) => {
      if (e.key === "trw-dark-mode") setIsDark(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return isDark;
}

// ─── Font Face Injection ──────────────────────────────────────────────────────
// Loads Poppins from /public/assets/fonts/ (your local woff2 files).
// Injected once into <head>; never re-runs.

// ─── Inline style token helpers (same pattern as PrivacyPolicy) ───────────────
// Every color decision goes through these — flip isDark and everything updates.

const tok = {
  pageBg:    (d) => ({ background: d ? "#000000" : "#ffffff" }),
  cardBg:    (d, step = 0) => ({
    // light: #f1f1f1 → #f9f9f9   dark: #1c1c1c → #262626
    background: d
      ? `hsl(0,0%,${11 + step * 1.5}%)`
      : `hsl(0,0%,${94.5 - step * 0.4}%)`,
  }),
  primary:   (d) => ({ color: d ? "#ffffff"                : "rgb(13,19,23)" }),
  muted:     (d) => ({ color: d ? "rgba(255,255,255,0.82)" : "rgba(13,19,23,0.76)" }),
  heading:   (d) => ({ color: d ? "#ffffff"                : "#11151a" }),
  red:             { color: "#cf2727" },
  redBright:       { color: "#ff1a1a" },
  redIcon:         { color: "#d12a2a" },
  font:            { fontFamily: "'Poppins', Helvetica, Arial, sans-serif" },
  transition:      "background 200ms, color 200ms",
};

// ─── Data ─────────────────────────────────────────────────────────────────────

const ENABLEMENT_ITEMS = [
  { title: "Expert Travel Insights", desc: "Data-backed, experience-driven and easy-to-understand guidance.",                                                   step: 0 },
  { title: "Unique Stays",           desc: "Handpicked boutique and experiential accommodations across the globe.",                                             step: 1 },
  { title: "Super Deals",            desc: "Curated, high-value travel deals for all types of travelers and destinations.",                                     step: 2 },
  { title: "Itineraries",            desc: "Plug-and-play trip plans crafted for seamless global travel experiences.",                                          step: 3 },
  { title: "Job Listings",           desc: "Verified hospitality and travel roles consolidated in one place for career explorers.",                              step: 4 },
];

const PRINCIPLES = [
  { icon: faGlobe,    title: "Travel with Purpose",    desc: "Travel intentionally, seek meaning, build connections, and let every journey shape your perspective." },
  { icon: faLandmark, title: "Be Culturally Curious",  desc: "Listen deeply, respect traditions, embrace differences, and learn from every culture you encounter." },
  { icon: faPenNib,   title: "Share the Wonder",       desc: "Tell authentic stories, capture real moments, and inspire others through meaningful travel experiences." },
  { icon: faCompass,  title: "Embrace the Unexpected", desc: "Welcome detours, adapt with confidence, and transform surprises into unforgettable travel memories." },
  { icon: faStore,    title: "Celebrate Local",        desc: "Support local communities, honor traditions, choose small businesses, and travel responsibly everywhere." },
  { icon: faRoute,    title: "Travel Your Way Better", desc: "Whether it is romance, adventure, work, or wanderlust, we help you find the best deals, tips, and gear." },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function PillBadge({ children }) {
  return (
    <div style={{
      display: "inline-flex",
      borderRadius: 9999,
      background: "linear-gradient(90deg,#a31419 0%,#f3484d 100%)",
      padding: "12px 24px",
    }}>
      <p style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: "0.08em", color: "#fff" }}>
        {children}
      </p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function AboutUs() {
  const isDark = useSyncedDarkMode();

  // Shared layout widths
  const container = { margin: "0 auto", width: "min(1500px, 100%)" };
  const autoGrid  = (min = 460) => ({
    display: "grid",
    gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
    alignItems: "center",
  });

  return (
    <div style={{ minHeight: "100vh", width: "100%", overflowX: "clip", ...tok.pageBg(isDark), ...tok.font, WebkitFontSmoothing: "antialiased", transition: tok.transition }}>

      {/* ── Hero Video Banner ── */}
      <section style={{ position: "relative", height: "clamp(420px,40vw,682px)", width: "100%", overflow: "hidden", background: "#000" }}>
        <video
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(1)" }}
          autoPlay loop muted playsInline preload="auto" aria-hidden="true"
        >
          <source src={videoWebm} type="video/webm" />
          <source src={videoMp4}  type="video/mp4" />
        </video>
        <div style={{ position: "relative", zIndex: 10, margin: "0 auto", maxWidth: 1500, height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 20px", textAlign: "center" }}>
          <h1 style={{ margin: 0, fontFamily: "'Poppins', Helvetica, Arial, sans-serif", fontSize: "clamp(42px,7vw,98px)", lineHeight: 0.98, letterSpacing: "-0.03em", color: "#fff" }}>
            <span style={{ fontWeight: 300 }}>Find</span>{" "}
            <span style={{ fontWeight: 600 }}>Stories.</span>{" "}
            <span style={{ fontWeight: 300 }}>Feel</span>{" "}
            <span style={{ fontWeight: 600 }}>Stories.</span>
          </h1>
        </div>
      </section>

      {/* ── Main Content ── */}
      <main style={{ display: "flex", flexDirection: "column", gap: 50, padding: "0 15px", ...tok.pageBg(isDark), transition: tok.transition }}>

        {/* ── Tagline ── */}
        <section style={{ ...container, marginTop: 50, textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: "clamp(22px,3vw,45px)", fontWeight: 300, lineHeight: 1.7, ...tok.primary(isDark), transition: tok.transition }}>
            At Travel.RethinkWays, we believe great travel pays you
            <br />back in stories and experiences. It's a smart investment of
            <br />your time, money and connections.
            <br />We're here to help you travel better, spend smarter and
            <br />unlock experiences beyond your imagination.
          </p>
        </section>

        {/* ── Who We Are ── */}
        <section style={container}>
          <div style={{ ...autoGrid(480), gap: 40 }}>
            {/* Left — text */}
            <div>
              <PillBadge>WHO WE ARE</PillBadge>
              <div style={{ marginTop: 20, display: "grid", gap: 16 }}>
                {[
                  "We are a team of travel enthusiasts, deal hunters and hospitality experts backed by years of expertise in the travel and tourism industry.",
                  "Travel Rethink Ways is a modern travel intelligence platform built for today's explorers. We simplify decision-making by combining curated insights, practical guides, trusted recommendations, and value-driven deals.",
                  "We bridge inspiration with clarity. From discovering destinations to unlocking the best stays, experiences, and savings, we help every journey feel intentional, informed, and rewarding.",
                ].map((text, i) => (
                  <p key={i} style={{ margin: 0, fontSize: "clamp(18px,1.6vw,22px)", lineHeight: "40px", fontWeight: 400, ...tok.muted(isDark), transition: tok.transition }}>
                    {text}
                  </p>
                ))}
              </div>
            </div>
            {/* Right — image */}
            <div style={{ display: "flex", alignItems: "start" }}>
              <img src={whoWeAreImg} alt="Our team collaborating" width={700} height={630}
                style={{ display: "block", width: "100%", height: "auto", objectFit: "contain", opacity: isDark ? 0.9 : 1, transition: "opacity 200ms" }}
                loading="lazy" fetchPriority="low" decoding="async" />
            </div>
          </div>
        </section>

        {/* ── Vision & Mission ── */}
        <section style={container}>
          <div style={{ ...autoGrid(440), gap: 32 }}>
            {[
              { heading: "Our Vision",   body: "Every person should have the chance to see the world and its wonders. Why? Because the joy of discovery belongs to everyone." },
              { heading: "Our Mission",  body: "To rethink the way the world travels delivering smarter choices, unbeatable deals, and the confidence to roam boldly." },
            ].map(({ heading, body }) => (
              <article key={heading} style={{ padding: 4 }}>
                <h2 style={{ margin: 0, fontSize: "clamp(26px,2.4vw,34px)", fontWeight: 700, textTransform: "uppercase", lineHeight: 1.15, letterSpacing: "-0.02em", ...tok.font, ...tok.heading(isDark), transition: tok.transition }}>
                  {heading}
                </h2>
                <p style={{ margin: "20px 0 0", fontSize: "clamp(18px,1.6vw,22px)", lineHeight: 1.9, fontWeight: 400, ...tok.muted(isDark), transition: tok.transition }}>
                  {body}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* ── Enablement ── */}
        <section style={container}>
          <div style={{ ...autoGrid(460), gap: 40 }}>
            {/* Left — big headline */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <PillBadge>ENABLEMENT</PillBadge>
              <h2 style={{ margin: "20px 0 0", fontSize: "clamp(42px,9vw,128px)", fontWeight: 900, lineHeight: 1.02, letterSpacing: "-0.02em", ...tok.font, ...tok.primary(isDark), transition: tok.transition }}>
                Where<br />Travel<br />Decisions<br />Come<br />Together.
              </h2>
            </div>
            {/* Right — cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {ENABLEMENT_ITEMS.map(({ title, desc, step }) => (
                <article key={title} style={{ borderRadius: 16, padding: "20px 28px", ...tok.cardBg(isDark, step), transition: tok.transition }}>
                  <h3 style={{ margin: 0, fontSize: "clamp(20px,1.8vw,24px)", lineHeight: "36px", fontWeight: 700, ...tok.primary(isDark), transition: tok.transition }}>
                    {title}
                  </h3>
                  <p style={{ margin: "8px 0 0", fontSize: "clamp(16px,1.4vw,18px)", lineHeight: "31px", fontWeight: 400, ...tok.muted(isDark), transition: tok.transition }}>
                    {desc}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Principles ── */}
        <section style={container}>
          <PillBadge>PRINCIPLES</PillBadge>
          <h2 style={{ margin: "20px 0 0", fontSize: "clamp(32px,4vw,58px)", lineHeight: 1.12, fontWeight: 700, ...tok.font, ...tok.primary(isDark), transition: tok.transition }}>
            How We <span style={tok.red}>Travel &amp; Inspire</span>
          </h2>
          <div style={{ marginTop: 32, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%,420px),1fr))", gap: "32px 40px" }}>
            {PRINCIPLES.map(({ icon, title, desc }) => (
              <article key={title}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <FontAwesomeIcon icon={icon} style={{ fontSize: 30, flexShrink: 0, ...tok.redIcon }} aria-hidden="true" />
                  <h3 style={{ margin: 0, fontSize: "clamp(20px,1.8vw,26px)", lineHeight: "39px", fontWeight: 700, ...tok.primary(isDark), transition: tok.transition }}>
                    {title}
                  </h3>
                </div>
                <p style={{ margin: "12px 0 0", paddingLeft: 46, fontSize: "clamp(16px,1.4vw,18px)", lineHeight: "31px", fontWeight: 400, ...tok.muted(isDark), transition: tok.transition }}>
                  {desc}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* ── Advertise With Us ── */}
        <section style={{ ...container, ...tok.pageBg(isDark), transition: tok.transition }}>
          <div style={{ ...autoGrid(460), gap: 32, minHeight: 500 }}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <img src={advertiseImg} alt="Advertise With Us" width={700} height={600}
                style={{ display: "block", width: "100%", maxWidth: 900, height: "auto", objectFit: "contain", opacity: isDark ? 0.9 : 1, transition: "opacity 200ms" }}
                loading="lazy" fetchPriority="low" decoding="async" />
            </div>
            <div style={{ paddingRight: 24 }}>
              <h2 style={{ margin: 0, fontSize: "clamp(32px,4vw,58px)", lineHeight: 1.06, fontWeight: 700, letterSpacing: "-0.04em", ...tok.font, ...tok.heading(isDark), transition: tok.transition }}>
                Advertise With Us
              </h2>
              {[
                "Travel.RethinkWays.com is built on connection. It's about the people, products, and services that make every journey better.",
                "We share that connection with brands who add genuine value to our audience's adventures.",
              ].map((text, i) => (
                <p key={i} style={{ margin: i === 0 ? "32px 0 0" : "24px 0 0", maxWidth: 760, fontSize: "clamp(18px,1.6vw,22px)", lineHeight: "40px", fontWeight: 400, ...tok.muted(isDark), transition: tok.transition }}>
                  {text}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contact Us ── */}
        <section style={{ ...container, paddingBottom: 50, ...tok.pageBg(isDark), transition: tok.transition }}>
          <div style={{ ...autoGrid(440), gap: 40, alignItems: "center" }}>
            <div>
              <h2 style={{ margin: 0, fontSize: "clamp(32px,4vw,58px)", lineHeight: 1.06, fontWeight: 700, letterSpacing: "-0.04em", ...tok.font, ...tok.heading(isDark), transition: tok.transition }}>
                Contact Us
              </h2>
              <p style={{ margin: "32px 0 0", maxWidth: 760, fontSize: "clamp(18px,1.6vw,22px)", lineHeight: "40px", fontWeight: 400, ...tok.muted(isDark), transition: tok.transition }}>
                Need help planning, partnering or promoting? Our team is here to connect.
              </p>
              <a href="mailto:travel@rethinkways.com"
                style={{ display: "inline-block", marginTop: 24, fontSize: "clamp(18px,1.6vw,22px)", lineHeight: "40px", fontWeight: 700, textDecoration: "none", ...tok.redBright }}>
                travel@rethinkways.com
              </a>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <img src={contactImg} alt="Travel Rethink Ways contact map" width={700} height={510}
                style={{ display: "block", width: "100%", maxWidth: 720, height: "auto", objectFit: "contain", opacity: isDark ? 0.9 : 1, transition: "opacity 200ms" }}
                loading="lazy" fetchPriority="low" decoding="async" />
            </div>
          </div>
        </section>

      </main>
    </div>
  );
}

export default AboutUs;
